import { ChangeDetectionStrategy, Component, computed, inject, OnInit, Signal, signal } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { BenefitSplitLimits } from '@my-account/const';
import { ForemanPromocodesService } from '@my-account/feature';
import { SdvorButtonComponent } from '@sdvor/shared/ui/v2';

@Component({
  selector: 'sd-promocode-generator',
  imports: [SdvorButtonComponent],
  templateUrl: './promocode-generator.component.html',
  styleUrl: './promocode-generator.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class PromocodeGeneratorComponent implements OnInit {
  private readonly foremanPromocodesService = inject(ForemanPromocodesService);

  /** Значение ползунка, выбранное пользователем. `null` — пользователь ещё не менял значение */
  private readonly selectedDiscountPercent = signal<number | null>(null);

  protected readonly limits: Signal<BenefitSplitLimits | undefined> = toSignal(
    this.foremanPromocodesService.getBenefitSplitLimits(),
  );

  protected readonly limitsLoading: Signal<boolean> = toSignal(
    this.foremanPromocodesService.getBenefitSplitLimitsLoading(),
    { initialValue: false },
  );

  /** Идёт генерация промокода */
  protected readonly generating: Signal<boolean> = toSignal(
    this.foremanPromocodesService.getGeneratePromocodeLoading(),
    { initialValue: false },
  );

  /** Скидка клиента, % */
  protected readonly discountPercent = computed(
    () => this.selectedDiscountPercent() ?? this.limits()?.defaultDiscountPercent ?? 0,
  );

  /** Мой бонус, % */
  protected readonly bonusPercent = computed(() => (this.limits()?.benefitPercent ?? 0) - this.discountPercent());

  /** Заполненная часть трека ползунка, % */
  protected readonly sliderProgress = computed(() => {
    const limits = this.limits();

    if (!limits) {
      return 0;
    }

    const range = limits.discountPercentMaximum - limits.discountPercentMinimum;

    return range > 0 ? ((this.discountPercent() - limits.discountPercentMinimum) / range) * 100 : 0;
  });

  /** Заполненная часть трека ползунка, доля от 0 до 1 (для позиционирования подписи под бегунком) */
  protected readonly sliderRatio = computed(() => this.sliderProgress() / 100);

  ngOnInit(): void {
    this.foremanPromocodesService.loadBenefitSplitLimits();
  }

  protected changeDiscountPercent(event: Event): void {
    this.selectedDiscountPercent.set(Number((event.target as HTMLInputElement).value));
  }

  /** Генерация промокода. Количество генераций не ограничено */
  protected generatePromocode(): void {
    if (this.generating() || !this.limits()) {
      return;
    }

    this.foremanPromocodesService.generatePromocode({
      discountPercent: this.discountPercent(),
      bonusPercent: this.bonusPercent(),
    });
  }
}
